import React from 'react';
import { AlertTriangle, Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * SECURITY: Session Timeout Warning Modal
 * Displays countdown when session is about to expire
 * Allows user to extend session with one click
 */
export function SessionTimeoutWarning({ 
  show, 
  remainingTime, 
  onExtend, 
  formatTime 
}) {
  if (!show) return null;

  const isUrgent = remainingTime <= 60; // Less than 1 minute

  return (
    <Dialog open={show} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            Session Expiring
          </DialogTitle>
          <DialogDescription>
            Your session will expire due to inactivity. Click below to stay logged in.
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          <div className={`text-center p-6 rounded-xl ${isUrgent ? 'bg-red-50 border-2 border-red-200' : 'bg-amber-50 border-2 border-amber-200'}`}>
            <Clock className={`w-12 h-12 mx-auto mb-3 ${isUrgent ? 'text-red-500' : 'text-amber-500'}`} />
            <p className={`text-4xl font-mono font-bold ${isUrgent ? 'text-red-600' : 'text-amber-600'}`}>
              {formatTime(remainingTime)}
            </p>
            <p className="text-sm text-slate-600 mt-2">
              {isUrgent ? 'Session expiring NOW!' : 'Time remaining'}
            </p>
          </div>

          <div className="mt-4 p-3 bg-slate-100 rounded-lg">
            <p className="text-xs text-slate-600 text-center">
              🔒 <strong>Security Notice:</strong> Sessions automatically expire after 30 minutes of inactivity to protect sensitive facility data.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button 
            onClick={onExtend} 
            className={`w-full ${isUrgent ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
            size="lg"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Stay Logged In
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SessionTimeoutWarning;
